import { Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { colors } from "brand";
import { formatVenueTime, isVenueTimezoneConfigured, VENUE_TIME_UNSET_TEXT } from "common-fe/venueTime";
import { formatPercentage, OPEN_DIVISION_KEY, placedByDivisionKey } from "score-common";
import PageShell from "../components/PageShell";
import { fetchClassResults, resolveClassId } from "../api";
import { isScorePubliclyVisible } from "common-fe/scoreboard/publicScoreVisibility.js";

// #1641 (design §3.1/§6.3) — the QR-code target: `/results/:showId/:year/
// :classNumber`, a public, no-poll (one-shot) read of a class's ranked
// results via apps/score-api/src/handlers/scoreboard-public.js's
// getPublicClassResults. Chrome'd by PageShell (the same branded frame the
// rest of this app's public pages use), NOT ScoreboardFrame — this is a
// normal scrollable page a rider lands on after scanning a QR code, not the
// full-bleed TV board.
//
// #1854 — the QR now encodes a short, human-readable class NUMBER (e.g.
// "246") rather than the internal fvd: classId, so this page is
// resolve-then-fetch: showId+classNumber -> classId via score-api's new
// public resolveClassByNumber route (api.js's resolveClassId), THEN the
// unchanged classId-keyed fetchClassResults. Two round trips per QR scan is
// fine — this is a one-shot read, not the 5s poll. Both calls share one
// error-rendering path below: every 404 from either call (show_not_found,
// view_not_found, class_not_found) reads the same generic "not available"
// way, since view_not_found is the show's exposure gate, not proof the
// class doesn't exist.
//
// `:year` DECISION (card #1641's payload-contract note 6): the endpoint
// does no year check at all (unlike fetchScoreboard's :year, which 404s on
// mismatch) — score-api's getPublicClassResults route has no year param.
// Chosen here: treat it as FE-side/cosmetic ONLY. It is never read from
// useParams below and never sent to fetchClassResults — validating it
// FE-side would just be a second, redundant place to encode "what year is
// this show," and a wrong year in a shared/bookmarked QR link would 404 the
// whole page for no reason the payload itself needs. The QR code this app
// generates (QRCorner) always encodes the current real year, so this only
// matters for a hand-edited or stale URL, where cosmetic is the right
// failure mode (a slightly wrong year in the address bar, not a broken
// page).
//
// The payload's `resultsFinal` / `resultsRevealed` / `placing` vs `rank`
// rules (design §4.5) are the same ones the card's judge pinned on the
// score-api handler — this page must not re-derive them from `ofCount`
// alone (see the handler's own header comment for why that pair is
// ambiguous).
//
// #1647-shape fix (card #1641 round 2 judge): `resultsRevealed` means the
// WHOLE class is scored (every non-scratched ride CONFIRMED/FINAL), not
// "there are publishable rows": rows are published regardless of
// resultsRevealed, so the ordinary mid-class state is `resultsRevealed:
// false` WITH a non-empty `results` array (design §6.2 mode type 6,
// "current class standings"). `resultsRevealed` is therefore not the right
// input to ANY wording branch here: the empty state keys off `results`
// being empty (nothing published yet, whatever the reason), and the
// final-vs-standings choice keys off `resultsFinal` alone.
function statusBanner({ resultsFinal, results = [] }) {
  if (results.length === 0) {
    return { text: "No results have been confirmed for this class yet.", severity: "info" };
  }
  if (resultsFinal) {
    return { text: "Final class results", severity: "success" };
  }
  return { text: "Current class standings", severity: "info" };
}

// Card #1694 AC4 — percent formatting goes through score-common's
// formatPercentage (3 decimals, "single source of truth for percentage
// rendering across the FEs" per its own doc comment) rather than a
// per-component `.toFixed()`. formatPercentage already returns "—" for a
// non-numeric/null composite, so this wrapper only exists to keep the call
// sites below reading as "score text" rather than a bare formatter name.
//
// Card #2285 -- this is the "final score page" AC1 targets: a composite
// strictly below 55 renders the same "—" a missing composite already does,
// via isScorePubliclyVisible (the one decision point every public-score
// suppression call site, here and on the scoreboard, delegates to). A
// deliberate product decision by Jaybill, 2026-09-04, reaffirmed
// 2026-09-05 directly on card #2285, that runs AGAINST USEF DR123.2 and
// DR123.3 (docs/rules/usef/rulebook-dr-dressage-2026.pdf, current 2026
// edition), which both require every score and every final result to be
// published with no low-score exception -- see
// ../util/publicScoreVisibility.js for the full rationale. Render-only:
// `row.composite` itself is never touched, only what this function prints.
export function scoreText(composite) {
  if (!isScorePubliclyVisible(composite)) return "—";
  return formatPercentage(composite);
}

// Round-2 judge (AC3) — the Place column's fallback used to be a bare
// `row.placing ?? row.rank`, which only guards against null/undefined:
// FVD's `0` "not yet placed" sentinel is neither, so `0 ?? row.rank`
// evaluated to the literal 0 and rendered it as if it were a real place.
// AC3 requires it render with NO place shown instead — this page's own
// established empty-value convention (competitorNumber/riderName/horseName
// all fall back to the same glyph a few lines below) is the em-dash, so a
// sentinel placing uses that too rather than a bare blank string or a
// hyphen the rest of the page doesn't use.
function placeText(row, resultsFinal) {
  if (!resultsFinal) return row.rank;
  if (row.placing === 0) return "—";
  // Card #2115 -- a terminated ride (No Show/Excused/Eliminated, #2104) is
  // normally never given a placing, so `placing` is null and the `?? rank`
  // fallback below would print its sort position as a Place ("Place 2 ·
  // No Show"). With a termReason and no stored placing it gets the same
  // em-dash a stored placing of 0 already gets. A stored placing > 0 is
  // real data and still prints verbatim. Render-only: row order, `rank`
  // and the other rows' places are untouched (the gap is intentional).
  if (row.placing == null && row.termReason) return "—";
  return row.placing ?? row.rank;
}

// Round-2 judge (AC3) — FVD's `0` placing is its "not yet placed" sentinel
// (measured from prod, card comment 2026-08-17: class 119 entry 445, Open,
// composite 66.03, placing 0, a non-scratched scored ride), not a real 4th
// place. Within one division's rows (already placing-then-composite
// ordered by score-api's compareResultsByPlacingThenComposite), pulls the
// placing:0 rows out and appends them AFTER every placed row, each side
// keeping its own incoming relative order (a stable partition, not a
// re-sort by score or name — nothing here recomputes a placing, it only
// moves where an already-stored 0 renders). Deliberately scoped to exactly
// `placing === 0`: `placing: null` (never placed at all) already sorts
// last through compareResultsByPlacingThenComposite's own `!= null` check,
// so it needs no help here, and reaching further than the literal 0
// sentinel AC3 names would be scope creep this diff doesn't need.
function sortGroupRows(rows) {
  const placed = [];
  const unplaced = [];
  for (const row of rows) {
    if (row?.placing === 0) unplaced.push(row);
    else placed.push(row);
  }
  return unplaced.length > 0 ? [...placed, ...unplaced] : rows;
}

// Card #3408 -- groups `results` under one bucket per division ONLY when the
// class's own Placed by division setting is on (`class.placedByDivision ===
// true`, the one per-class fact the placing code and the scoreboard also
// read). The old guesses (the class division string listing 2+ statuses, #1693)
// are gone: a class whose division string lists several statuses but is not
// Placed by division is placed as one list, so it shows one list.
//
// When on, groups are the distinct rider statuses present on the rows
// (placedByDivisionKey: trim + case-fold, so "Open"/" open " are one group),
// and a ride with no rider status counts as Open. Open leads, then the other
// groups in the order their first row appears (rows arrive in
// placing-then-composite order). A group's label is its first row's own
// trimmed status text. When off, always exactly one group with `label: null`,
// rendered with no heading (the pre-#1693 flat table). Nothing is dropped
// (AC4 of #1693: never suppress a stored placing), and every group's rows pass
// through sortGroupRows so the 0 sentinel sorts after the placed riders.
export function groupResultsByDivision(placedByDivision, results) {
  const rows = Array.isArray(results) ? results : [];
  if (placedByDivision !== true) {
    return [{ key: "all", label: null, rows: sortGroupRows(rows) }];
  }

  const byKey = new Map();
  for (const row of rows) {
    const key = placedByDivisionKey(row?.riderStatus);
    if (!byKey.has(key)) {
      const raw = typeof row?.riderStatus === "string" ? row.riderStatus.trim() : "";
      byKey.set(key, { key, label: key === OPEN_DIVISION_KEY || raw === "" ? "Open" : raw, rows: [] });
    }
    byKey.get(key).rows.push(row);
  }
  const groups = [...byKey.values()];
  const openIndex = groups.findIndex((group) => group.key === OPEN_DIVISION_KEY);
  if (openIndex > 0) groups.unshift(...groups.splice(openIndex, 1));
  for (const group of groups) group.rows = sortGroupRows(group.rows);
  return groups;
}

export default function ClassResultsPage() {
  const { showId, classNumber } = useParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // #1854 — resolve-then-fetch: the URL only ever carries the
      // human-readable classNumber, so classId is resolved fresh on every
      // load (including Refresh) rather than cached across renders — this
      // is a one-shot QR-scan read, not a poll, so the extra round trip
      // costs nothing and never risks reusing a stale classId.
      const resolved = await resolveClassId({ showId, classNumber });
      const result = await fetchClassResults({ showId, classId: resolved.classId });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, classNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const banner = data ? statusBanner(data) : null;
  const classTitle = data?.className || "Class results";
  const classSubtitle = data?.classNumber != null ? `Class ${data.classNumber}` : null;

  return (
    <PageShell>
      <Box sx={{ width: "100%" }}>
        {loading && !data && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress data-testid="class-results-loading" />
          </Box>
        )}

        {error && !data && (
          <Alert severity="error" data-testid="class-results-error" sx={{ mb: 2 }}>
            {/* Every 404 (show_not_found, view_not_found, class_not_found)
                reads the same way here on purpose — #1641's payload-contract
                note 3: view_not_found is the show's exposure gate, not proof
                the class doesn't exist, so this must never claim
                non-existence from the error code alone. */}
            Results aren&apos;t available for this class right now.
          </Alert>
        )}

        {data && (
          <>
            <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 700 }}>
              {classTitle}
            </Typography>
            {classSubtitle && (
              <Typography variant="body2" sx={{ color: "text.secondary", mb: data.judgesPositions?.length ? 0.5 : 2 }}>
                {classSubtitle}
              </Typography>
            )}

            {/* Card #1694 AC3 — judge names + booth letters. Omitted from
                the payload entirely (never an empty array) when the class
                has no judge assignments on file, so this renders nothing
                rather than an empty "Judges:" line. */}
            {data.judgesPositions?.length ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
                data-testid="class-results-judges"
              >
                {data.judgesPositions
                  .map(({ position, judges }) => `${position}: ${(judges || []).join(", ")}`)
                  .join(" · ")}
              </Typography>
            ) : null}

            {/* Card #2570 — the class's own sponsor (#2565's classItem.
                sponsorId, distinct from a scoreboard mode's sponsorId the
                TV board's SponsorStrip renders). score-api's
                getPublicClassResults resolves it through the same
                resolveSponsorEnvelope the board already uses, which
                collapses BOTH an unset sponsorId and a dangling one (the
                sponsor row deleted, #2593 known gap: nothing currently
                clears sponsorId off a class) to `sponsor: null` alike, so
                this component never has to tell the two apart. Same
                missing-data rule as SponsorStrip (common-fe/scoreboard/
                SponsorStrip.jsx): no sponsor, or a sponsor with no square
                image, renders nothing at all, never an empty box or a
                broken <img>. */}
            {data.sponsor?.squareUrl ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  width: "fit-content",
                  maxWidth: "100%",
                }}
                data-testid="class-results-sponsor"
              >
                <Box
                  component="img"
                  src={data.sponsor.squareUrl}
                  alt=""
                  data-testid="class-results-sponsor-image"
                  sx={{ width: 48, height: 48, flexShrink: 0, objectFit: "contain" }}
                />
                {data.sponsor.textLine ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                    data-testid="class-results-sponsor-text"
                  >
                    {data.sponsor.textLine}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <Alert severity={banner.severity} sx={{ mb: 2 }} data-testid="class-results-banner">
              {banner.text}
            </Alert>

            {data.results.length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small" data-testid="class-results-table">
                  <TableHead>
                    <TableRow>
                      {/* #1641 payload-contract note 2: never label `rank`
                          "place"/"placing" before resultsFinal — only the
                          FINAL header/value switches to the committed
                          placing. */}
                      <TableCell sx={{ color: "text.secondary" }}>
                        {data.resultsFinal ? "Place" : "Rank"}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>#</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>Rider</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>Horse</TableCell>
                      <TableCell sx={{ color: "text.secondary" }} align="right">
                        Score
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Card #1693 (option A, division headers) — a
                        single-division class (the common case, AC2) is one
                        group with `label: null`, so no heading row ever
                        renders here and the markup is identical to the
                        pre-#1693 flat table. */}
                    {groupResultsByDivision(data.placedByDivision, data.results).map((group) => (
                      <Fragment key={group.key}>
                        {group.label ? (
                          <TableRow data-testid="class-results-division-heading">
                            <TableCell
                              colSpan={5}
                              sx={{ color: "text.primary", fontWeight: 700, pt: 2, borderBottom: "none" }}
                            >
                              {group.label}
                            </TableCell>
                          </TableRow>
                        ) : null}
                        {group.rows.map((row) => (
                          <TableRow key={row.rideId} data-testid="class-results-row">
                            <TableCell sx={{ color: "text.primary", fontWeight: 700 }}>
                              {placeText(row, data.resultsFinal)}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.competitorNumber ?? "—"}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.riderName ?? "—"}
                              {typeof row.section === "string" && row.section !== "" ? (
                                // #3296: the rider's equitation section as the
                                // show names it ("Heat B"), a label only. Places
                                // stay one list for the whole class; a rider not
                                // drawn has no `section` key and shows nothing.
                                <Typography
                                  component="span"
                                  variant="body2"
                                  data-testid="class-results-section"
                                  sx={{ ml: 1, color: "text.secondary" }}
                                >
                                  {row.section}
                                </Typography>
                              ) : null}
                              {row.riderStatus ? (
                                <Chip
                                  label={row.riderStatus}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1, color: "text.secondary" }}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.horseName ?? "—"}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }} align="right">
                              {row.termReason ? (
                                // Card #2104 AC2 — a terminated ride shows its
                                // specific DR123.3 outcome (Scratched/Excused/
                                // Eliminated/No Show) INSTEAD OF the percentage,
                                // never both. Same field + read pattern #2060
                                // put on the TV board's RankedList: rendered
                                // verbatim, never re-derived here.
                                <span data-testid="class-results-term-reason">{row.termReason}</span>
                              ) : (
                                scoreText(row.composite)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {data.ofCount} of {data.entryCount} {data.entryCount === 1 ? "ride" : "rides"} shown
              {" · "}
              {/* #3324 -- score-api sends venueTimezone null when the show has no
                  usable zone. Say so plainly (no error styling, the public pages
                  never show errors) rather than a Pacific time that is a guess. */}
              {isVenueTimezoneConfigured(data.venueTimezone)
                ? `as of ${formatVenueTime(data.serverTime, { timeZone: data.venueTimezone })} venue time`
                : VENUE_TIME_UNSET_TEXT}
            </Typography>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            onClick={load}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            sx={{ color: colors.foxOrange }}
            data-testid="class-results-refresh"
          >
            Refresh
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}
